'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';

export const TOOLBAR_MENU_OPEN_ATTR = 'data-toolbar-menu-open';

function setToolbarMenuOpen(controlBar: HTMLElement, isOpen: boolean) {
  if (isOpen) {
    controlBar.setAttribute(TOOLBAR_MENU_OPEN_ATTR, 'true');
    controlBar.dataset.moreOptionsOpen = 'true';
    return;
  }

  requestAnimationFrame(() => {
    const hasOpenMenu = document.querySelector(
      'body > .em-reactions-menu, body > .more-options-dropdown.em-more-options-dropdown',
    );
    if (!hasOpenMenu) {
      controlBar.removeAttribute(TOOLBAR_MENU_OPEN_ATTR);
      delete controlBar.dataset.moreOptionsOpen;
    }
  });
}

function syncControlBarMenuOpen(anchor: HTMLElement | null, isOpen: boolean) {
  const controlBar = anchor?.closest('.lk-control-bar') as HTMLElement | null;
  if (!controlBar) return () => {};

  setToolbarMenuOpen(controlBar, isOpen);

  return () => {
    setToolbarMenuOpen(controlBar, false);
  };
}

export function useControlBarMenuAnchor(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement>,
) {
  const [menuRect, setMenuRect] = React.useState<DOMRect | null>(null);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    return syncControlBarMenuOpen(anchorRef.current, isOpen);
  }, [isOpen, anchorRef]);

  React.useEffect(() => {
    if (!isOpen) {
      setMenuRect(null);
      return;
    }

    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      setMenuRect(el.getBoundingClientRect());
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen, anchorRef]);

  return { mounted, menuRect };
}

type ControlBarAnchoredDropdownProps = {
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  menuRef?: React.Ref<HTMLDivElement>;
};

export function ControlBarAnchoredDropdown({
  isOpen,
  anchorRef,
  children,
  className,
  style,
  menuRef,
}: ControlBarAnchoredDropdownProps) {
  const { mounted, menuRect } = useControlBarMenuAnchor(isOpen, anchorRef);

  if (!mounted || !isOpen || !menuRect) return null;

  return createPortal(
    <div
      ref={menuRef}
      className={className}
      style={{
        position: 'fixed',
        left: menuRect.left + menuRect.width / 2,
        top: menuRect.top - 12,
        transform: 'translate(-50%, -100%)',
        zIndex: 10050,
        ...style,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
